import camelcaseKeys from 'camelcase-keys'
import snakecaseKeys from 'snakecase-keys'

/**
 * Case-mapping layer for the API-client boundary.
 *
 * THE WIRE FORMAT STAYS snake_case. The Rails backend is untouched. This module
 * is the single seam where snake_case (wire) ⇄ camelCase (app) conversion
 * happens: `deepCamelize` runs on every response body inside
 * `services/api/client.ts`, and `deepDecamelize` runs on every request body and
 * query-string param before it leaves the client. Nothing outside
 * `services/api/` ever sees a snake_case field name.
 *
 * Both directions are deep and pass non-plain values through untouched
 * (FormData / Blob / File / URLSearchParams / ArrayBuffer views / Dates / null /
 * primitives) so binary uploads and streamed bodies are never enumerated.
 */

/**
 * Response object-maps whose KEYS ARE DATA, not field names — camelCasing them
 * would corrupt the data. Paths are dot-notation from the response root, using
 * the ORIGINAL (snake_case) key names, and matched by `camelcase-keys`
 * `stopPaths`. A stopPath still camelCases the *field itself* (e.g.
 * `by_type` → `byType`); it only halts recursion INTO the value, preserving the
 * value's own keys. For a map inside an array the path omits the index and
 * applies to every element (per camelcase-keys docs).
 *
 * NOTE: numeric-string keys (e.g. `{ "123": 4 }`) are preserved automatically by
 * camelcase-keys, so quantity maps keyed by card id (`UserQuantities.*`) and
 * integer-keyed distributions (`DeckStats.costCurve`,
 * `DeckStats.powerDistribution`) need NO stopPath — they are safe by
 * construction. Maps with mangle-able string keys (hyphenated/underscored/
 * capitalized) MUST be listed here: `quantities_by_base` is keyed by
 * card_image_id values like "OP01-001", which camelCasing silently mangles to
 * "op01001" — breaking every `byBase[card.cardImageId]` lookup.
 */
const CAMELIZE_STOP_PATHS = [
  // GET /features → { features: { <flipper_feature_name>: boolean } }.
  // Keys are Flipper feature names (e.g. `sealed_products`) — DATA.
  // NOTE: this path also matches admin GET /admin/features, where `features`
  // is an ARRAY of flag objects — recursion stops there too, so its
  // created_at/updated_at stay snake_case. Currently unread by the admin UI;
  // if that UI ever needs those fields, scope this stop or camelize on read.
  'features',
  // GET /decks/:id/stats → { color_distribution: { <color>: count } }.
  // Keys are OPTCG color names ("Red", "Green"); camelCase would lowercase them.
  'color_distribution',
  // GET /decks/:id/stats → { type_breakdown: { <card_type>: count } }.
  // Keys are card types ("Leader", "Character"); camelCase would mangle them.
  'type_breakdown',
  // GET /dashboard → { sealed: { by_type: { <product_type>: count } } }.
  // Keys are sealed product types ("Booster Box"); camelCase would mangle them.
  'sealed.by_type',
  // Defensive: the same sealed-stats shape at a response root (no current
  // endpoint returns it there, but keeps the stop robust if one is added).
  'by_type',
  // GET /collection/quantities_by_base → { quantities_by_base: { <card_image_id>: [entries] } }.
  // Keys are card_image_id values ("OP01-001") — DATA. Unlike the other stops,
  // the VALUES are entry objects the app reads in camelCase; deepCamelize
  // re-camelizes them after this stop protects the keys (see below).
  'quantities_by_base',
  // GET /notifications → { notifications: [{ data: { … } }] }.
  // `data` is an opaque per-notification payload bag (backend-defined dynamic
  // keys like `triggered_price`, `subscribable_id`); preserved as sent.
  'notifications.data',
  // Admin job endpoints → `arguments` is the serialized ActiveJob argument
  // payload (dynamic keys). Preserve verbatim for the admin job inspector.
  'failed_jobs.arguments',
  'failed_job.arguments',
  'recurring_tasks.arguments',
  'pending_jobs.arguments',
  'processing_jobs.arguments',
  'completed_jobs.arguments',
] as const

/** True for objects we should walk (plain objects + arrays); false for
 * primitives, null, and host/binary objects (FormData, Blob, File,
 * URLSearchParams, ArrayBuffer views, Date, Map, Set, …) which must pass
 * through untouched. */
function isTransformable(value: unknown): value is Record<string, unknown> | unknown[] {
  if (value === null || typeof value !== 'object') return false
  if (Array.isArray(value)) return true
  const proto = Object.getPrototypeOf(value)
  // Only literal `{}`-style objects (Object.prototype or a null prototype).
  return proto === Object.prototype || proto === null
}

/**
 * Recursively convert a value's object keys to camelCase (wire → app). Applied
 * to every response body. Data-keyed maps in {@link CAMELIZE_STOP_PATHS} keep
 * their key semantics; non-plain values pass through unchanged.
 */
export function deepCamelize<T = unknown>(value: unknown): T {
  if (!isTransformable(value)) return value as T
  const result = camelcaseKeys(value, {
    deep: true,
    stopPaths: CAMELIZE_STOP_PATHS as unknown as readonly string[],
  })
  // quantities_by_base is stopPathed so its top-level DATA keys ("OP01-001")
  // survive intact, but its values are arrays of entry objects the app reads in
  // camelCase — camelize each value now that the map keys are out of reach.
  if (!Array.isArray(result)) {
    const map = (result as Record<string, unknown>).quantitiesByBase
    if (isTransformable(map) && !Array.isArray(map)) {
      ;(result as Record<string, unknown>).quantitiesByBase = Object.fromEntries(
        Object.entries(map).map(([key, entries]) => [
          key,
          isTransformable(entries)
            ? camelcaseKeys(entries as readonly Record<string, unknown>[], { deep: true })
            : entries,
        ]),
      )
    }
  }
  return result as T
}

/**
 * Recursively convert a value's object keys to snake_case (app → wire). Applied
 * to every request body and query-param object. Non-plain values (FormData,
 * Blob, …) pass through unchanged so file uploads are never enumerated.
 *
 * No stopPaths needed on this side: request payloads are app-authored and
 * carry only field names, never dynamic data-keyed maps. Idempotent on keys
 * that are already snake_case, so it is safe to run during the incremental
 * migration while some call sites still pass snake_case bodies.
 */
export function deepDecamelize<T = unknown>(value: unknown): T {
  if (!isTransformable(value)) return value as T
  return snakecaseKeys(value as Record<string, unknown>, { deep: true }) as T
}
